import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import { useAuthStore } from '../../store/authStore';
import { getCentralAppUrl } from '../../config/authDomain';

/**
 * Sign-in gate for actions that create or save something.
 *
 * The app is browsable signed-out: labs, studios, swatches, operation catalogs
 * and model pickers are all public, and the backend serves them anonymously.
 * Authentication is required only at the point where work would be created or
 * stored — which is also the point where it costs money and needs an owner to
 * scope it to.
 *
 * Callers wrap the action rather than hiding the UI, so a signed-out visitor can
 * still see what an operation does and configure it; they're asked to sign in
 * when they press go.
 */
interface SignInGate {
  /** True when the caller is signed in and may create. */
  canCreate: boolean;
  /**
   * Runs `action` when signed in; otherwise opens the sign-in prompt and
   * returns false so the caller can abort.
   */
  requireSignIn: (action?: () => void) => boolean;
  /** Opens the prompt directly, for surfaces that need their own copy. */
  promptSignIn: () => void;
}

const SignInGateContext = createContext<SignInGate | null>(null);

/**
 * Remembers that a visitor has already seen and dismissed the welcome prompt, so
 * it appears once rather than on every page they open.
 */
const WELCOME_SEEN_KEY = 'ct_v3_welcome_prompt_seen';

export const SignInGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  // checkAuth() runs asynchronously on mount, so there is a window where nobody
  // is authenticated *yet*. Treating that as signed-out prompts people who
  // already have a session.
  const isResolving = useAuthStore(state => state.isLoading);
  const [open, setOpen] = useState(false);
  // Distinguishes the unprompted first-visit invitation from a prompt the user
  // triggered by trying to create something — the wording differs.
  const [isWelcome, setIsWelcome] = useState(false);
  const centralAppUrl = getCentralAppUrl();

  // First visit: invite the visitor to sign in or sign up, but let them decline
  // and carry on. Only once, and never for someone already signed in.
  useEffect(() => {
    if (isResolving || isAuthenticated) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem(WELCOME_SEEN_KEY) === 'true';
    } catch {
      // Private browsing or blocked storage — show the prompt rather than crash.
    }
    if (!seen) {
      setIsWelcome(true);
      setOpen(true);
    }
  }, [isResolving, isAuthenticated]);

  // Signing in is the thing the prompt asks for, so completing it must close the
  // prompt. Without this the dialog outlives the session it was asking to
  // create: the redirect returns, auth resolves, and the overlay is still there
  // with no way to tell it the answer arrived.
  useEffect(() => {
    if (isAuthenticated) {
      setOpen(false);
      setIsWelcome(false);
    }
  }, [isAuthenticated]);

  const dismiss = useCallback(() => {
    setOpen(false);
    if (isWelcome) {
      try {
        window.localStorage.setItem(WELCOME_SEEN_KEY, 'true');
      } catch {
        // Not being able to remember is survivable; the prompt is dismissible.
      }
      setIsWelcome(false);
    }
  }, [isWelcome]);

  const promptSignIn = useCallback(() => {
    if (isAuthenticated || isResolving) return;
    setIsWelcome(false);
    setOpen(true);
  }, [isAuthenticated, isResolving]);

  const requireSignIn = useCallback(
    (action?: () => void) => {
      if (isAuthenticated) {
        action?.();
        return true;
      }
      // Don't accuse someone of being signed out before we know. The caller
      // still aborts, but silently — the window is a few hundred ms.
      if (!isResolving) {
        setIsWelcome(false);
        setOpen(true);
      }
      return false;
    },
    [isAuthenticated, isResolving],
  );

  const value = useMemo<SignInGate>(
    () => ({ canCreate: isAuthenticated, requireSignIn, promptSignIn }),
    [isAuthenticated, requireSignIn, promptSignIn],
  );

  const goToSignIn = () => {
    if (!centralAppUrl) {
      window.location.href = `/dev-auth-helper?returnTo=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`;
      return;
    }
    // Come back through /sync-session, not through the page we are on. It is the
    // only route that reads a token out of the URL, so a redirect straight back
    // here lands a signed-in user on a page that never learns they signed in —
    // which is exactly what happened on coralcreator.com, where there is also no
    // shared cookie to fall back on.
    const returnTo = window.location.pathname + window.location.search;
    const callback = `${window.location.origin}/sync-session?returnTo=${encodeURIComponent(returnTo)}`;
    window.location.href = `${centralAppUrl}/login?redirectTo=${encodeURIComponent(callback)}`;
  };

  return (
    <SignInGateContext.Provider value={value}>
      {children}
      <Dialog open={open} onClose={dismiss} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isWelcome ? 'Welcome to Coral Creator' : 'Sign in to create'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isWelcome
              ? 'Sign in or create an account to generate images, documents and video, and keep what you make. You can also look around first — everything is browsable without an account.'
              : 'You can explore the labs and everything they can do without an account. Sign in to generate and save work — it needs somewhere to belong to.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismiss} sx={{ textTransform: 'none' }}>
            {isWelcome ? 'Continue without signing in' : 'Keep looking'}
          </Button>
          <Button variant="contained" onClick={goToSignIn} sx={{ textTransform: 'none' }}>
            {isWelcome ? 'Sign in or sign up' : 'Sign in'}
          </Button>
        </DialogActions>
      </Dialog>
    </SignInGateContext.Provider>
  );
};

/**
 * Falls back to a permissive gate when no provider is mounted, so a component
 * rendered outside the app shell (tests, storybook) behaves as before rather
 * than throwing.
 */
export function useRequireSignIn(): SignInGate {
  const ctx = useContext(SignInGateContext);
  // Read the store rather than the AuthContext: this hook is used inside lab
  // panels, which are rendered without an AuthProvider in tests, and useAuth
  // throws when the provider is absent.
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  return (
    ctx ?? {
      canCreate: isAuthenticated,
      requireSignIn: (action?: () => void) => {
        action?.();
        return true;
      },
      promptSignIn: () => undefined,
    }
  );
}
